import { memo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { classNames } from "shared/lib/classNames/classNames";
import { SociometryState } from "shared/types/sociometry";
import { RootState } from "redux/rootReducer";
import {
    loadSociometryTest,
    selectAnswer,
    nextQuestion,
    prevQuestion,
    submitAnswers,
    resetSociometry,
} from "redux/actions/setSociometryActions";
import { Layout } from "components/Layout";
import { Loader } from "components/Loader";
import { SociometryQuestion } from "components/sociometry";

import cls from "./SociometryTestPage.module.scss";

interface SociometryTestPageProps {
    className?: string;
}

export const SociometryTestPage = memo((props: SociometryTestPageProps) => {
    const { className } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const { id: sessionId } = useParams<{ id: string }>();

    const { user } = useSelector((state: RootState) => state.auth);
    const sociometry: SociometryState = useSelector((state: RootState) => state.sociometry);

    const { questions, classmates, answers, currentQuestionIndex, submitting, error } = sociometry;

    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestionIndex];
    const isFirstQuestion = currentQuestionIndex === 0;
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    const selectedClassmates = currentAnswer?.chosen || [];
    const hasValidSelection = selectedClassmates.filter((id) => id && id.length > 0).length >= 1;

    const paddedCurrent = String(currentQuestionIndex + 1).padStart(2, "0");
    const paddedTotal = String(questions.length).padStart(2, "0");

    useEffect(() => {
        dispatch(loadSociometryTest(sessionId));

        return () => {
            dispatch(resetSociometry());
        };
    }, [sessionId, dispatch]);

    const handleSelectionChange = useCallback(
        (chosen: string[]) => {
            if (currentQuestion) {
                dispatch(selectAnswer(currentQuestion.questionIndex, currentQuestion.isPositive, chosen));
            }
        },
        [dispatch, currentQuestion],
    );

    const handleBack = useCallback(() => {
        dispatch(prevQuestion());
    }, [dispatch]);

    const handleNext = useCallback(async () => {
        if (isLastQuestion) {
            const filteredAnswers = answers.map((answer) => ({
                ...answer,
                chosen: answer.chosen.filter((id) => id && id.length > 0),
            }));

            const success = await dispatch(submitAnswers(sessionId, user?._id || "", filteredAnswers) as any);

            if (success) {
                history.push("/dashboard");
            }
        } else {
            dispatch(nextQuestion());
        }
    }, [dispatch, isLastQuestion, answers, sessionId, user, history]);

    if (error) {
        return (
            <Layout>
                <div className={cls.errorContainer}>
                    <p className={cls.errorText}>{error}</p>
                    <button className={cls.retryButton} onClick={() => dispatch(loadSociometryTest(sessionId))}>
                        Попробовать снова
                    </button>
                </div>
            </Layout>
        );
    }

    if (!currentQuestion || questions.length === 0) {
        return <Loader />;
    }

    return (
        <Layout hideNav>
            <div className="book-page">
                <div className="book-spread">
                    <div className="book-left">
                        <button
                            className={`book-left__back${!isFirstQuestion ? " book-left__back--active" : ""}`}
                            onClick={handleBack}
                            disabled={isFirstQuestion}
                        >
                            <svg viewBox="0 0 24 24" width="16" height="16">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                            </svg>
                            <span>Предыдущий вопрос</span>
                        </button>

                        <div className="book-left__icons">
                            <a className="book-left__icon" href="/dashboard">
                                <svg viewBox="0 0 24 24" width="20" height="20">
                                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                                </svg>
                            </a>
                            <a className="book-left__icon" href="/settings">
                                <svg viewBox="0 0 24 24" width="20" height="20">
                                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    <div className="book-main">
                        <div className="book-main__counter">
                            {paddedCurrent}/{paddedTotal}
                        </div>

                        <SociometryQuestion
                            question={currentQuestion}
                            classmates={classmates}
                            selectedClassmates={selectedClassmates}
                            onSelectionChange={handleSelectionChange}
                        />

                        <div className={cls.nextBtnWrapper}>
                            <button
                                type="button"
                                className={classNames(cls.nextBtn, { [cls.nextBtnDisabled]: !hasValidSelection || submitting })}
                                onClick={handleNext}
                                disabled={!hasValidSelection || submitting}
                            >
                                <svg className={cls.checkIcon} viewBox="0 0 24 24" width="20" height="20" fill="none">
                                    <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span className={cls.nextBtnText}>Готово</span>
                            </button>
                        </div>
                    </div>

                    <div className="book-right-stubs">
                        <div className="book-right-stub" />
                        <div className="book-right-stub book-right-stub--2" />
                    </div>
                </div>
            </div>
        </Layout>
    );
});

SociometryTestPage.displayName = "SociometryTestPage";
